// Pure helpers for mapping Finnish postal codes → main travel region.
// Regions follow the Visit Finland 4-region model surfaced to international visitors.

export type RegionKey = "lapland" | "lakeland" | "archipelago" | "coast";

export const REGION_ORDER: RegionKey[] = ["lapland", "lakeland", "archipelago", "coast"];

export const REGION_LABELS: Record<RegionKey, string> = {
  lapland: "Lapland",
  lakeland: "Lakeland",
  archipelago: "Archipelago",
  coast: "Coast of Finland",
};

export function regionLabel(key: string | null | undefined): string | null {
  if (!key) return null;
  const k = String(key).toLowerCase().trim() as RegionKey;
  return REGION_LABELS[k] ?? null;
}

/**
 * Extract a Finnish 5-digit postal code from a free-form string
 * (e.g. an address). Returns null if none found.
 */
export function extractPostalCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const m = String(input).match(/\b(\d{5})\b/);
  return m ? m[1] : null;
}

/**
 * Given a Finnish postal code, return the main travel region key.
 * Rules:
 *   Lapland: 94–99 (+ exception: 93600 Ruka/Kuusamo)
 *   Archipelago: 10, 20–23, 26–29, 48–49, 65–68, 90–93
 *   Coast of Finland: 00–02, 06 (capital/Helsinki region is marketed as coastal)
 *   Lakeland: everything else valid
 */
export function regionFromPostalCode(postal: string | null | undefined): RegionKey | null {
  if (!postal) return null;
  const raw = String(postal).replace(/\D/g, "");
  if (raw.length < 4) return null;
  const code = raw.padStart(5, "0").slice(0, 5);

  // Exception first — Ruka/Kuusamo is in Lapland even though 93 is Archipelago.
  if (code === "93600") return "lapland";

  const p2 = Number(code.slice(0, 2));
  if (p2 >= 94 && p2 <= 99) return "lapland";

  if (
    p2 === 10 ||
    (p2 >= 20 && p2 <= 23) ||
    (p2 >= 26 && p2 <= 29) ||
    p2 === 48 || p2 === 49 ||
    (p2 >= 65 && p2 <= 68) ||
    (p2 >= 90 && p2 <= 93)
  ) {
    return "archipelago";
  }

  if ([0, 1, 2, 6].includes(p2)) return "coast";

  return "lakeland";
}
